/**
 * Observer bootstrap - slim entry point for the professional read-only observer build.
 *
 * This file is the observer equivalent of main.ts. It registers only the plugins
 * needed for read-only observation. Everything parent-specific is excluded:
 * - No VitePWA / Service Worker
 * - No Raygun with parent user context
 * - No AppInsights with parent connection string
 * - No Rewardful referral plugin
 * - No form validation rules (no write forms in observer)
 * - No parent stores (LoginStore, CommonStore, MessagesStore, etc.)
 * - No parent httpClient with token refresh interceptors
 */

import { createApp } from 'vue'
import { createPinia } from 'pinia'
import piniaPluginPersistedstate from 'pinia-plugin-persistedstate'

import ObserverApp from './ObserverApp.vue'
import router from './router/observerRoutes'

import VueDOMPurifyHTML from 'vue-dompurify-html'

import { Settings } from 'luxon'

import i18n from './i18n'

// FontAwesome
import { library, type IconDefinition } from '@fortawesome/fontawesome-svg-core'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { all } from '@talkingparents/kit-4ad9ec01d5/icons'

library.add(...(all as IconDefinition[]))

// Luxon defaults
Settings.throwOnInvalid = true
Settings.defaultWeekSettings = { firstDay: 7, minimalDays: 1, weekend: [6, 7] }

// Create app
const app = createApp(ObserverApp)

// Pinia (with persisted state for session store)
const pinia = createPinia()
pinia.use(piniaPluginPersistedstate)
app.use(pinia)

// Router
app.use(router)

// i18n
app.use(i18n)

// DOMPurify
app.use(VueDOMPurifyHTML, {
  default: {
    ALLOWED_TAGS: ['em']
  }
})

// Toastify (for observer-safe notifications like "Session extended")
import Vue3Toastify, { toast, type ToastContainerOptions } from 'vue3-toastify'
import 'vue3-toastify/dist/index.css'

app.use(Vue3Toastify, {
  autoClose: 3000,
  closeButton: false,
  position: toast.POSITION.TOP_CENTER,
  icon: false,
  transition: {
    enter: 'toast-enter',
    exit: 'toast-exit',
    collapse: false
  },
  clearOnUrlChange: false
} as ToastContainerOptions)

// Shared directives (read-only safe)
import { ObserveVisibility } from 'vue-observe-visibility'

app.directive('observe-visibility', {
  beforeMount: (el, binding, vnode) => {
    ;(vnode as any).context = binding.instance
    ObserveVisibility.bind(el, binding, vnode)
  },
  updated: ObserveVisibility.update,
  unmounted: ObserveVisibility.unbind
})

app.directive('skeleton', (el, binding) => {
  const value = binding.value

  if (value === undefined || value === null) {
    el.classList.remove('skeleton')
    return
  }

  const isBoolean = typeof value === 'boolean'
  const isObj = typeof value === 'object'

  const loading = isBoolean
    ? value
    : isObj && 'loading' in value && !!value.loading

  const minWidth = isObj && 'minWidth' in value ? Number(value.minWidth) : 4.5

  const maxWidth = isObj && 'maxWidth' in value ? Number(value.maxWidth) : 10

  if (loading) {
    el.classList.add('skeleton')

    if (!el.style.getPropertyValue('--skeleton-width').length) {
      const finalWidth = Math.random() * (maxWidth - minWidth) + minWidth
      el.style.setProperty('--skeleton-width', `${finalWidth}rem`)
    }
  } else {
    el.classList.remove('skeleton')
  }
})

app.directive('inline', (element) => {
  element.replaceWith(...element.children)
})

// Global components
app.component('FontAwesomeIcon', FontAwesomeIcon)

import loading from '@/components/library/Loading.vue'
// eslint-disable-next-line vue/multi-word-component-names
app.component('loading', loading)

// V3 Scroll Lock
import V3ScrollLock from 'v3-scroll-lock'
app.use(V3ScrollLock, {})

// Mount immediately - no feature flag fetch needed for POC
// Keep windowWidth in sync for responsive breakpoints
import { useCommonStore } from '@/stores/CommonStore'
const commonStore = useCommonStore()
window.addEventListener('resize', () => {
  commonStore.$patch({ windowWidth: window.innerWidth })
})

app.mount('#app')
